// Tell a long-lived tab when it's running yesterday's app.
//
// The `_headers` file already stops the HTML shell being cached, so a RELOAD
// always gets the current build. What nothing handled is a tab left open across a
// deploy: an SPA keeps its bundle until the page reloads, so the operator sees a
// version of the product that no longer exists. On 2026-07-29 that produced three
// "this feature is missing" reports in a row against features that had shipped
// hours earlier.
//
// Detection needs no build plumbing: fetch the shell (uncached) and compare the
// main asset filename it references with the one this page actually loaded. Vite
// content-hashes those filenames, so a difference IS a new build.

import { useEffect, useState } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

function runningAsset() {
  const el = [...document.querySelectorAll('script[src]')]
    .map((s) => s.getAttribute('src'))
    .find((src) => /\/assets\/index-.*\.js$/.test(src || ''));
  return el ? el.split('/').pop() : null;
}

async function deployedAsset() {
  // cache: 'no-store' rather than trusting the header — a proxy in between might
  // not honour no-cache, and a false "up to date" is the failure we're fixing.
  const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const html = await res.text();
  const m = html.match(/\/assets\/(index-[A-Za-z0-9_-]+\.js)/);
  return m ? m[1] : null;
}

/**
 * @returns { stale } — true when a newer build is live than the one running here.
 */
export function useAppVersion() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const mine = runningAsset();
    // Dev server has no hashed bundle; nothing to compare, nothing to warn about.
    if (!mine) return undefined;

    let cancelled = false;
    const check = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      try {
        const live = await deployedAsset();
        if (!cancelled && live && live !== mine) setStale(true);
      } catch {
        // Offline or a blip — say nothing. Claiming "out of date" because a fetch
        // failed would train operators to ignore the banner.
      }
    };

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    // A tab that's been in the background all day is exactly the stale case, so
    // re-check the moment someone looks at it again.
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return { stale };
}
